import { hasMeaningfulTyreSerial } from './tyre-parse.js';

/**
 * @param {unknown} serial
 * @returns {string}
 */
export function normalizeTyreSerial(serial) {
  return String(serial ?? '').trim();
}

/**
 * Parse GET /wheels/:series/:sn/tyres response into serial strings.
 * @param {unknown} data
 * @returns {string[]}
 */
export function parseSeasonPoolPayload(data) {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .map(entry => (entry && typeof entry === 'object' ? entry.tyreSerial : entry))
    .filter(serial => hasMeaningfulTyreSerial(String(serial ?? '')))
    .map(String);
}

/**
 * Läge B: scanned serial is OK when it exists in the car's season pool (läge A).
 * Wheel position is UI-only — not matched against a fixed slot assignment.
 *
 * @param {string} scannedSerial
 * @param {string[]} poolSerials
 * @param {string} [position]
 * @returns {{ ok: boolean, expected: string, actual: string, position: string }}
 */
export function verifyTyreAgainstPool(scannedSerial, poolSerials, position = '') {
  const actual = normalizeTyreSerial(scannedSerial);
  const pool   = Array.isArray(poolSerials)
    ? poolSerials.map(normalizeTyreSerial).filter(Boolean)
    : [];
  const ok     = actual.length > 0 && pool.includes(actual);

  return {
    ok,
    expected: '',
    actual,
    position
  };
}

/**
 * Simulate läge A scans appending to a pool, then läge B checks per corner.
 * Useful in tests and dev tooling.
 *
 * @param {string[]} pool — mutates in place
 * @param {string} serial
 * @returns {'registered'|'duplicate'}
 */
export function mockRegisterTyreScan(pool, serial) {
  const normalized = normalizeTyreSerial(serial);

  if (!hasMeaningfulTyreSerial(normalized)) {
    throw new Error('Invalid tyre serial');
  }

  if (pool.includes(normalized)) {
    return 'duplicate';
  }

  pool.push(normalized);
  return 'registered';
}

/**
 * @param {string[]} pool
 * @param {Record<string, string>} cornerScans — e.g. { frontLeft: '123', ... }
 * @returns {Record<string, { ok: boolean, actual: string }>}
 */
export function mockVerifyCornerScans(pool, cornerScans) {
  /** @type {Record<string, { ok: boolean, actual: string }>} */
  const results = {};

  for (const [position, serial] of Object.entries(cornerScans)) {
    const { ok, actual } = verifyTyreAgainstPool(serial, pool, position);
    results[position] = { ok, actual };
  }

  return results;
}
